import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'

import Footer from 'modules/core/components/Sidepanel/Footer'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import type StepApprover from 'modules/approvals/store/models/StepApprover'
import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import ApproveExpensableButton from 'modules/expenses/components/expenses/Form/Buttons/ApproveExpensableButton'
import DeleteExpensableButtons from 'modules/expenses/components/expenses/Form/Buttons/DeleteExpensableButtons'
import ReimburseExpensableThroughSepaButton from 'modules/expenses/components/expenses/Form/Buttons/ReimburseExpensableThroughSepaButton'
import RejectExpensableButton from 'modules/expenses/components/expenses/Form/Buttons/RejectExpensableButton'
import SendExpensableForApprovalButton from 'modules/expenses/components/expenses/Form/Buttons/SendExpensableForApprovalButton'
import ToggleExpensablePaidStatusButton from 'modules/expenses/components/expenses/Form/Buttons/ToggleExpensablePaidStatusButton'
import ToggleExpensablePayrollStatusButton from 'modules/expenses/components/expenses/Form/Buttons/ToggleExpensablePayrollStatusButton'
import UpdateButton from 'modules/expenses/components/expenses/Form/Buttons/UpdateButton'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import {
  isAutomaticExpensable,
  isChangesRequestedExpensable,
  isDeletableExpensable,
  isDraftExpensable,
  isExpensableReporter,
  isOwnExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

interface EditExpenseFooterActionsProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  expensable: Expensable
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  customizationFields: CustomizationField[]
  canEdit: boolean
  canOverrideApprovalFlow: boolean
  canDeleteExpensable: boolean
  canSetExpensableToPaid: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  stepApprovers: StepApprover[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  formValid?: boolean
  formDirty?: boolean

  trigger: () => Promise<boolean>
  onClose: () => void
}

const EditExpenseFooterActions = ({
  formType,
  expensable,
  formValues,
  customizationFields,
  canEdit,
  canOverrideApprovalFlow,
  canDeleteExpensable,
  canSetExpensableToPaid,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  stepApprovers,
  taxAgencyOrganization,
  formValid,
  formDirty,
  trigger,
  onClose,
}: EditExpenseFooterActionsProps) => {
  const [isUpdating, setUpdating] = useState(false)
  const [isSending, setSending] = useState(false)
  const [isDeleting, setDeleting] = useState(false)
  const [isRejecting, setRejecting] = useState(false)
  const [isApproving, setApproving] = useState(false)
  const [isPaidStatusChanging, setPaidStatusChanging] = useState(false)
  const [isPayrollStatusChanging, setPayrollStatusChanging] = useState(false)
  const [isSepaStatusChanging, setSepaStatusChanging] = useState(false)

  // Reset all button states when expensable changes
  // We want to avoid stale states in flows where the sidepanel does not close
  // Flows such as "restart approval flow" or "set as paid"
  useEffect(() => {
    setUpdating(false)
    setSending(false)
    setDeleting(false)
    setRejecting(false)
    setApproving(false)
    setPaidStatusChanging(false)
    setPayrollStatusChanging(false)
    setSepaStatusChanging(false)
  }, [expensable])

  const { section } = useOutletContext<RouteContextParams>()
  const { employee: currentEmployee } = useCurrentSession()
  const isDraft = isDraftExpensable(expensable)
  const isPending: boolean = isPendingExpensable(expensable)
  const isChangesRequested = isChangesRequestedExpensable(expensable)

  const isOwner = isOwnExpensable(expensable, currentEmployee)
  const isReporter = isExpensableReporter(expensable, currentEmployee)

  const isCardPaymentPending = expensable?.expense?.cardPayment?.status === 'pending'
  const isAutomaticExpensePending = isAutomaticExpensable(expensable) && isCardPaymentPending
  const isPendingApproval =
    isPending && expensable.currentUserApprovalStatus === 'pending' && !isCardPaymentPending
  const isOwnerOrReporter = isOwner || isReporter

  const expenseIsReviewable = isPendingApproval && !isMyExpensesSection(section)
  const canReviewWithOverridePermission =
    isPending && canOverrideApprovalFlow && !isAutomaticExpensePending

  const isReviewable = expenseIsReviewable || canReviewWithOverridePermission
  const showSendForApproval = (isDraft || isChangesRequested) && canEdit && !expensable.groupId

  const isSubmitting =
    isUpdating ||
    isSending ||
    isDeleting ||
    isRejecting ||
    isApproving ||
    isPaidStatusChanging ||
    isPayrollStatusChanging ||
    isSepaStatusChanging

  const isReporterOrOwnerAndDraft = isOwnerOrReporter && isDraft
  const isOwnerAndChangesRequested = isOwner && isChangesRequested
  const canBeEditedAndIsPending = canEdit && isPending
  // TODO: Try to move into policy
  const showSaveButton =
    isReporterOrOwnerAndDraft || isOwnerAndChangesRequested || canBeEditedAndIsPending

  const showDelete =
    isDeletableExpensable(expensable, canDeleteExpensable, currentEmployee) || expensable.groupId

  const canReviewStep = expensable.currentUserApprovalStatus == 'pending'
  const showApproveButton =
    (canReviewStep || canOverrideApprovalFlow) && isPending && !isCardPaymentPending

  const canUpdateAndApprove = canBeEditedAndIsPending && formDirty

  const showRejectButton = isPending && (canReviewStep || canOverrideApprovalFlow)

  const canRequestChanges = canReviewStep || !canOverrideApprovalFlow

  const showToggleExpensablePaidStatusButton =
    (expensable.status == 'approved' || expensable.status == 'paid') && canSetExpensableToPaid

  const showToggleExpensablePayrollStatusButton =
    (expensable.status === 'approved' || expensable.status === 'in_payroll') &&
    expensable.type !== 'perdiem' &&
    reimburseThroughPayrollEnabled

  const showReimburseThroughSepaButton =
    expensable.status === 'approved' &&
    reimburseThroughSepaEnabled &&
    !reimburseThroughPayrollEnabled

  const canAnyAction =
    showSaveButton ||
    showDelete ||
    showRejectButton ||
    showSendForApproval ||
    showApproveButton ||
    showToggleExpensablePaidStatusButton ||
    showToggleExpensablePayrollStatusButton ||
    showReimburseThroughSepaButton

  if (!canAnyAction) {
    return null
  }

  return (
    <Box aria-label="footer-actions" position="sticky" style={{ bottom: '0' }}>
      <Footer
        content={
          <Box
            width="full"
            flexDirection="row"
            justifyContent={canEdit ? 'spaceBetween' : 'flexEnd'}
            alignItems="center"
            paddingX="s8"
            gap="s8"
          >
            <Box flexDirection="row" gap="s8">
              {showSaveButton && (
                <UpdateButton
                  formType={formType}
                  expensable={expensable}
                  formIsDirty={formDirty ?? false}
                  formValues={formValues}
                  taxAgencyOrganization={taxAgencyOrganization}
                  customizationFields={customizationFields}
                  canBeReviewed={isReviewable}
                  onClose={onClose}
                  onLoadingChange={setUpdating}
                  disabled={isSubmitting}
                />
              )}
              {showDelete && (
                <DeleteExpensableButtons
                  canDeleteExpensable={canDeleteExpensable}
                  expensable={expensable}
                  onClose={onClose}
                  onLoadingChange={setDeleting}
                  disabled={isSubmitting}
                />
              )}
            </Box>
            <Box flexDirection="row" gap="s8">
              {showRejectButton && (
                <RejectExpensableButton
                  expensable={expensable}
                  canOverrideApprovalFlow={canOverrideApprovalFlow}
                  canRequestChanges={canRequestChanges}
                  stepApprovers={stepApprovers}
                  onLoadingChange={setRejecting}
                  disabled={isSubmitting}
                />
              )}
              {showSendForApproval && (
                <SendExpensableForApprovalButton
                  formType={formType}
                  customizationFields={customizationFields}
                  formValues={formValues}
                  formValid={formValid ?? false}
                  expensable={expensable}
                  taxAgencyOrganization={taxAgencyOrganization}
                  onClose={onClose}
                  trigger={trigger}
                  onLoadingChange={setSending}
                  disabled={isSubmitting}
                />
              )}

              {showApproveButton && (
                <ApproveExpensableButton
                  formType={formType}
                  expensable={expensable}
                  stepApprovers={stepApprovers}
                  canOverrideApprovalFlow={canOverrideApprovalFlow}
                  canUpdateAndApprove={canUpdateAndApprove}
                  formValues={formValues}
                  formDirty={formDirty}
                  formValid={formValid}
                  customizationFields={customizationFields}
                  taxAgencyOrganization={taxAgencyOrganization}
                  trigger={trigger}
                  onClose={onClose}
                  onLoadingChange={setApproving}
                  disabled={isSubmitting}
                />
              )}
            </Box>
            {showToggleExpensablePaidStatusButton && (
              <ToggleExpensablePaidStatusButton
                expensable={expensable}
                canSetToPaid={canSetExpensableToPaid}
                canReimburseThroughPayroll={showToggleExpensablePayrollStatusButton}
                onLoadingChange={setPaidStatusChanging}
                disabled={isSubmitting}
              />
            )}
            {showToggleExpensablePayrollStatusButton && (
              <ToggleExpensablePayrollStatusButton
                expensable={expensable}
                reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
                reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                onLoadingChange={setPayrollStatusChanging}
                disabled={isSubmitting}
              />
            )}
            {showReimburseThroughSepaButton && (
              <ReimburseExpensableThroughSepaButton
                expensable={expensable}
                reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                onLoadingChange={setSepaStatusChanging}
                disabled={isSubmitting}
              />
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default EditExpenseFooterActions
